import { useState, useCallback, useRef } from "react";
import { api } from "../App";
import { Button } from "./ui/button";
import {
  Upload,
  X,
  Loader2,
  Camera,
  Star,
  GripVertical,
  ImagePlus,
  Info,
} from "lucide-react";
import { toast } from "sonner";

/**
 * MultiImageUpload
 * Props:
 *  - value: array of { id, url, data, order } (controlled)
 *  - onChange: (images[]) => void
 *  - minImages (default 3)
 *  - maxImages (default 10)
 *  - imageType (string) sent to backend
 *  - helperText: string
 *  - coverIndex (number) — index treated as the cover photo
 *  - onCoverChange: (idx) => void
 */
export default function MultiImageUpload({
  value = [],
  onChange,
  minImages = 3,
  maxImages = 10,
  imageType = "vehicle_photo",
  helperText = "Sube fotos claras de tu auto (frente, atrás, lados, interior)",
  coverIndex = 0,
  onCoverChange,
}) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const commit = (next) => {
    onChange?.(next);
  };

  const readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const processFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;

    const accepted = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    const valid = list.filter((f) => accepted.includes(f.type));
    const rejected = list.length - valid.length;
    if (rejected > 0) toast.error(`${rejected} archivo(s) no válidos (solo JPG/PNG/WEBP)`);

    const roomLeft = maxImages - value.length;
    const toUpload = valid.slice(0, roomLeft);
    if (valid.length > roomLeft) {
      toast.error(`Máximo ${maxImages} fotos (ignoradas ${valid.length - roomLeft})`);
    }
    if (!toUpload.length) return;

    setUploading(true);
    setProgress(0);
    try {
      const dataUrls = [];
      for (let i = 0; i < toUpload.length; i++) {
        const f = toUpload[i];
        if (f.size > 15 * 1024 * 1024) {
          toast.error(`${f.name} es muy grande (máx 10MB)`);
          continue;
        }
        const dataUrl = await readFileAsDataUrl(f);
        dataUrls.push(dataUrl);
        setProgress(Math.round(((i + 1) / toUpload.length) * 50));
      }

      if (!dataUrls.length) return;

      const { data } = await api.post("/upload/images/batch", {
        images: dataUrls,
        image_type: imageType,
      });
      setProgress(100);

      const uploaded = (data.images || []).map((img) => ({
        id: img.image_id,
        url: img.url,
        data: img.data,
      }));
      commit([...value, ...uploaded]);
      toast.success(`${uploaded.length} foto(s) subida(s)`);
    } catch (err) {
      console.error(err);
      toast.error("Error al subir fotos");
    } finally {
      setUploading(false);
      setTimeout(() => setProgress(0), 600);
    }
  };

  // Drop zone handlers
  const onDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);
  const onDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);
  const onDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);
  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);
      if (e.dataTransfer?.files) processFiles(e.dataTransfer.files);
    },
    [value, maxImages]
  );

  const remove = (idx) => {
    const next = value.filter((_, i) => i !== idx);
    commit(next);
    if (onCoverChange && coverIndex >= next.length) {
      onCoverChange(Math.max(0, next.length - 1));
    } else if (onCoverChange && idx < coverIndex) {
      onCoverChange(Math.max(0, coverIndex - 1));
    }
  };

  const makeCover = (idx) => {
    onCoverChange?.(idx);
  };

  // Reorder via HTML5 DnD
  const handleItemDragStart = (idx) => (e) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = "move";
    try {
      e.dataTransfer.setData("text/plain", String(idx));
    } catch (err) {
      /* ignore */
    }
  };
  const handleItemDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };
  const handleItemDrop = (targetIdx) => (e) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === targetIdx) {
      setDragIdx(null);
      return;
    }
    const next = [...value];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(targetIdx, 0, moved);
    commit(next);

    if (onCoverChange) {
      let newCover = coverIndex;
      if (dragIdx === coverIndex) newCover = targetIdx;
      else if (dragIdx < coverIndex && targetIdx >= coverIndex) newCover = coverIndex - 1;
      else if (dragIdx > coverIndex && targetIdx <= coverIndex) newCover = coverIndex + 1;
      onCoverChange(newCover);
    }
    setDragIdx(null);
  };

  const count = value.length;
  const atMax = count >= maxImages;
  const meetsMin = count >= minImages;

  return (
    <div className="space-y-4" data-testid="multi-image-upload">
      {/* Helper */}
      <div className="flex items-start gap-2 text-sm text-neutral-600">
        <Info className="w-4 h-4 mt-0.5 text-neutral-500 flex-shrink-0" />
        <div>
          <p>{helperText}</p>
          <p className="text-xs text-neutral-500 mt-0.5">
            Mínimo {minImages} · Máximo {maxImages} · JPG, PNG o WEBP
          </p>
        </div>
      </div>

      {/* Progress bar */}
      {uploading && (
        <div className="w-full bg-neutral-100 rounded-full h-1.5 overflow-hidden">
          <div
            className="bg-[#111111] h-1.5 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      {/* Grid previews */}
      {count > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {value.map((img, idx) => {
            const isCover = idx === coverIndex;
            return (
              <div
                key={img.id || idx}
                draggable
                onDragStart={handleItemDragStart(idx)}
                onDragOver={handleItemDragOver}
                onDrop={handleItemDrop(idx)}
                className={`relative aspect-square rounded-xl overflow-hidden group border-2 transition ${
                  isCover ? "border-[#111111]" : "border-transparent"
                } ${dragIdx === idx ? "opacity-50" : ""}`}
                data-testid={`photo-${idx}`}
              >
                <img
                  src={img.data || img.url}
                  alt={`Foto ${idx + 1}`}
                  className="w-full h-full object-cover pointer-events-none select-none"
                  draggable={false}
                />

                {/* Drag handle */}
                <div className="absolute top-2 left-2 w-6 h-6 bg-black/50 rounded-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing">
                  <GripVertical className="w-3.5 h-3.5 text-white" />
                </div>

                {/* Cover badge */}
                {isCover && (
                  <div className="absolute bottom-2 left-2 right-2 bg-[#111111] text-white text-xs font-semibold py-1 px-2 rounded-md text-center flex items-center justify-center gap-1">
                    <Star className="w-3 h-3 fill-current" />
                    Portada
                  </div>
                )}

                {/* Cover set button (when not cover) */}
                {!isCover && (
                  <button
                    type="button"
                    onClick={() => makeCover(idx)}
                    className="absolute bottom-2 left-2 right-2 bg-white/95 hover:bg-white text-[#111111] text-xs font-medium py-1 px-2 rounded-md opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1"
                    data-testid={`set-cover-${idx}`}
                  >
                    <Star className="w-3 h-3" />
                    Hacer portada
                  </button>
                )}

                {/* Delete */}
                <button
                  type="button"
                  onClick={() => remove(idx)}
                  className="absolute top-2 right-2 w-6 h-6 bg-black/60 hover:bg-black rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`remove-photo-${idx}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>

                {/* Order indicator */}
                <div className="absolute top-2 right-10 bg-black/60 text-white text-xs px-1.5 py-0.5 rounded-md font-medium">
                  {idx + 1}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Dropzone */}
      {!atMax && (
        <div
          onDragEnter={onDragEnter}
          onDragLeave={onDragLeave}
          onDragOver={onDragOver}
          onDrop={onDrop}
          className={`rounded-2xl border-2 border-dashed p-8 text-center transition ${
            dragOver
              ? "border-[#111111] bg-neutral-50"
              : "border-neutral-300 hover:border-neutral-400"
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            multiple
            className="hidden"
            onChange={(e) => processFiles(e.target.files)}
            data-testid="multi-image-file-input"
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => processFiles(e.target.files)}
            data-testid="multi-image-camera-input"
          />

          <div className="flex flex-col items-center">
            <div className="w-14 h-14 rounded-2xl bg-neutral-100 flex items-center justify-center mb-3">
              {uploading ? (
                <Loader2 className="w-6 h-6 text-neutral-600 animate-spin" />
              ) : (
                <ImagePlus className="w-6 h-6 text-neutral-600" />
              )}
            </div>
            <p className="text-sm font-semibold text-neutral-900 mb-1">
              {uploading
                ? "Subiendo fotos..."
                : dragOver
                ? "Suelta para subir"
                : "Arrastra fotos o haz clic"}
            </p>
            <p className="text-xs text-neutral-500 mb-4">
              {count}/{maxImages} fotos · {meetsMin ? "Listo para publicar" : `Faltan ${minImages - count}`}
            </p>

            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="border-neutral-200"
                data-testid="multi-image-pick-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Elegir archivos
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => cameraInputRef.current?.click()}
                disabled={uploading}
                className="border-neutral-200 sm:hidden"
                data-testid="multi-image-camera-btn"
              >
                <Camera className="w-4 h-4 mr-2" />
                Tomar foto
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Minimum validation warning */}
      {count > 0 && !meetsMin && (
        <p className="text-xs text-amber-600 flex items-center gap-1">
          <Info className="w-3.5 h-3.5" />
          Se requieren al menos {minImages} fotos para publicar el listado
        </p>
      )}
    </div>
  );
}
